import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Search, Phone, FileDown } from "lucide-react";
import { api, formatErr, formatPrice, STATUS_LABELS } from "@/lib/api";
import { useSEO, useSettings } from "@/lib/hooks";
import { toast } from "sonner";

export default function TrackingPage() {
  const { requestNumber } = useParams();
  const s = useSettings();
  const [number, setNumber] = useState(requestNumber || "");
  const [phone, setPhone] = useState("");
  const [data, setData] = useState(null);
  useSEO({ title: "Suivi de demande — Depann7" });

  const search = async (e) => {
    if (e) e.preventDefault();
    if (!number) return;
    try {
      const r = await api.get(`/track/${number}`, { params: phone ? { phone } : {} });
      setData(r.data);
    } catch (err) { toast.error(formatErr(err)); setData(null); }
  };

  useEffect(() => { if (requestNumber) search(); /* eslint-disable-next-line */ }, [requestNumber]);

  const inputCls = "w-full h-12 px-4 bg-surface-50 border border-surface-200 rounded-xl text-txt-900 text-sm placeholder:text-txt-300 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all";

  return (
    <div className="bg-white min-h-[60vh]">
      <div className="bg-ink-900 text-white py-14">
        <div className="max-w-6xl mx-auto px-4 lg:px-6">
          <h1 className="text-3xl lg:text-4xl font-extrabold">Suivi de demande</h1>
          <p className="text-ink-300 mt-2">Saisissez votre numéro de demande pour suivre l'avancement.</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 lg:px-6 py-12">
        <form onSubmit={search} className="bg-white border border-surface-200 rounded-2xl p-6 shadow-card grid sm:grid-cols-[1fr_1fr_auto] gap-3" data-testid="track-form">
          <input className={inputCls} placeholder="N° demande (ex: DPA-2026-0001)" value={number} onChange={(e) => setNumber(e.target.value)} data-testid="track-number" />
          <input className={inputCls} placeholder="Téléphone (optionnel)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="track-phone" />
          <button className="bg-brand hover:bg-brand-600 text-white font-bold rounded-xl px-5 h-12 inline-flex items-center justify-center gap-2 transition-colors" data-testid="track-search-btn"><Search size={16} /> Rechercher</button>
        </form>

        {data && (
          <div className="mt-6 bg-white border border-surface-200 rounded-2xl p-6 shadow-card" data-testid="track-result">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs text-txt-500 font-medium">Demande</div>
                <div className="text-3xl font-extrabold text-brand">{data.request_number}</div>
              </div>
              <div className="bg-brand-50 text-brand font-bold rounded-xl px-4 py-2 text-sm">{STATUS_LABELS[data.status] || data.status}</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm text-txt-700">
              <div><span className="text-txt-500">Service :</span> {data.service_type}</div>
              <div><span className="text-txt-500">Prix estimé :</span> {formatPrice(data.estimated_price)}</div>
              <div><span className="text-txt-500">Départ :</span> {data.pickup_address}</div>
              <div><span className="text-txt-500">Arrivée :</span> {data.dropoff_address || "—"}</div>
            </div>
            <div className="mt-6">
              <div className="font-bold text-txt-900 mb-3">Historique</div>
              <ul className="space-y-2">
                {(data.history || []).map((h, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-txt-700">
                    <span className="w-2 h-2 rounded-full bg-brand shrink-0" />
                    <span className="font-semibold">{STATUS_LABELS[h.new_status] || h.new_status}</span>
                    <span className="text-txt-500 text-xs">{(h.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={`tel:${s?.company_phone_raw || ""}`} className="bg-brand hover:bg-brand-600 text-white font-bold rounded-xl px-5 py-3 inline-flex items-center gap-2 transition-colors"><Phone size={16} /> Appeler</a>
              <button disabled className="border border-surface-200 rounded-xl px-5 py-3 inline-flex items-center gap-2 text-txt-300 cursor-not-allowed"><FileDown size={16} /> Facture (bientôt)</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
